import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Box,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import {
  lcCreateUsuario,
  lcListCuentas,
  lcUpdateUsuario,
  searchClienteServicio,
  getClienteServicioById,
} from "../services/leasecontrolUsuarios.service";

// Requisitos exigidos por Wialon/LeaseControl: 8-100 caracteres, especial, número, mayúscula y minúscula.
const PASSWORD_HINT =
  "8–100 caracteres, con mayúscula, minúscula, número y carácter especial";

function validatePasswordChange(newPass) {
  const wants = !!(newPass || "").trim();
  if (!wants) return null;

  const pass = newPass.trim();

  if (pass.length < 8 || pass.length > 100) {
    return "La contraseña debe tener entre 8 y 100 caracteres.";
  }
  if (!/[!-/:-@[-`{-~]/.test(pass)) {
    return "La contraseña debe incluir al menos 1 carácter especial.";
  }
  if (!/[0-9]/.test(pass)) {
    return "La contraseña debe incluir al menos 1 número.";
  }
  if (!/[A-Z]/.test(pass)) {
    return "La contraseña debe incluir al menos 1 letra mayúscula.";
  }
  if (!/[a-z]/.test(pass)) {
    return "La contraseña debe incluir al menos 1 letra minúscula.";
  }

  return null;
}

export default function LeasecontrolUsuarioDialog({
  open,
  onClose,
  mode,
  initialUser,
  onSuccess,
  providerName = "LeaseControl",
  createUsuario = lcCreateUsuario,
  updateUsuario = lcUpdateUsuario,
  // Cuando se crea un usuario desde el contexto de UN cliente puntual (ej.
  // Historial de Cliente), ya se sabe a que cliente/servicio va a quedar
  // vinculado — se pre-carga y se oculta el buscador "Cliente / Servicio"
  // en vez de pedir que lo busquen de nuevo. {cod_servicio_cliente,
  // nombre_cliente, descripcion} | undefined.
  initialClienteServicio,
  // Al editar: si el usuario está vinculado a uno o más servicios (un mismo usuario puede compartirse entre varios clientes), el campo
  // "Cliente / Servicio" pasa a listarlos todos, de solo lectura.
  vinculaciones,
}) {
  const isEdit = mode === "edit";
  const mostrarVinculacionesFijas = isEdit && Array.isArray(vinculaciones) && vinculaciones.length > 0;
  // Solo Leasecontrol (no Wialon) usa cuentas al crear un usuario.
  const usaCuentas = !isEdit && createUsuario === lcCreateUsuario;

  // CUENTA: dónde queda el usuario en la jerarquía de Leasecontrol (se crea bajo el administrador de esa cuenta).
  const [cuentas, setCuentas] = useState([]);
  const [cuentaSel, setCuentaSel] = useState(null);
  const [cuentasCargando, setCuentasCargando] = useState(false);
  // Avisos de Leasecontrol tras crear (p. ej. un permiso que no se pudo aplicar): el usuario ya quedó creado, solo se informa.
  const [avisosCreacion, setAvisosCreacion] = useState([]);
  const [cuentasInfo, setCuentasInfo] = useState({ razon_social: "", sucursal: null });
  // Opción "cuenta nueva": se crea una cuenta con el nombre (razón social) del cliente y el usuario queda como su administrador.
  const [cuentaNueva, setCuentaNueva] = useState(null);
  const opcionNueva = useMemo(
    () => (cuentaNueva?.disponible ? { id: "nueva", nueva: true, nombre: cuentaNueva.nombre, manager_id: true, usuarios: 0 } : null),
    [cuentaNueva]
  );
  const opcionesCuenta = useMemo(() => (opcionNueva ? [opcionNueva, ...cuentas] : cuentas), [opcionNueva, cuentas]);

  // CREATE
  const [codServicioCliente, setCodServicioCliente] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // EDIT
  const [userId, setUserId] = useState("");
  const [editName, setEditName] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [editCodServicio, setEditCodServicio] = useState("");

  // ✅ Change password (optional) -> solo nueva
  const [newPassword, setNewPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Autocomplete cliente-servicio
  const [csInput, setCsInput] = useState("");
  const [csOptions, setCsOptions] = useState([]);
  const [csLoading, setCsLoading] = useState(false);
  const [csSelected, setCsSelected] = useState(null);

  // Inicializar modal al abrir
  useEffect(() => {
    if (!open) return;

    setErr("");
    setAvisosCreacion([]);

    if (isEdit) {
      const id = initialUser?.id;
      const nm = initialUser?.nm || "";
      const localEstado = initialUser?.local?.estado; // "Activo" / "Inactivo"
      const codServicio = initialUser?.local?.cod_servicio_cliente;

      setUserId(String(id || ""));
      setEditName(String(nm));
      setEditCodServicio(String(codServicio ?? ""));

      const localNombre = initialUser?.local?.nombre_cliente;
      const localDesc = initialUser?.local?.descripcion;

      if (codServicio != null) {
        setCsSelected({
          cod_servicio_cliente: codServicio,
          nombre_cliente: localNombre || "",
          descripcion: localDesc || "",
        });
      } else {
        setCsSelected(null);
      }

      setCsInput(
        localNombre || (codServicio != null ? `ServicioCliente #${codServicio}` : "")
      );

      // Preferimos el flag real de Wialon (bit 0x1 de "fl" = deshabilitado) sobre el
      // espejo local, que puede desincronizarse si el estado cambia fuera de esta app.
      const wialonFlags = initialUser?.fl ?? initialUser?.usuario?.fl;
      const liveEnabled =
        typeof wialonFlags === "number" ? (wialonFlags & 1) === 0 : null;

      setEnabled(
        liveEnabled !== null
          ? liveEnabled
          : localEstado
          ? String(localEstado).toLowerCase() === "activo"
          : true
      );

      // ✅ limpiamos solo nueva pass
      setNewPassword("");
    } else {
      setName("");
      setPassword("");
      setCsOptions([]);

      if (initialClienteServicio?.cod_servicio_cliente != null) {
        setCodServicioCliente(String(initialClienteServicio.cod_servicio_cliente));
        setCsSelected(initialClienteServicio);
        setCsInput(initialClienteServicio.nombre_cliente || "");
      } else {
        setCodServicioCliente("");
        setCsSelected(null);
        setCsInput("");
      }
    }
  }, [open, isEdit, initialUser, initialClienteServicio]);

  // Label para mostrar: nombre_cliente y descripcion
  const getCsLabel = (opt) => {
    if (!opt) return "";
    const nom = (opt.nombre_cliente || "").trim();
    const desc = (opt.descripcion || "").trim();

    if (nom) return desc ? `${nom} — ${desc}` : nom;

    if (opt.cod_servicio_cliente != null)
      return `ServicioCliente #${opt.cod_servicio_cliente}`;

    return "";
  };

  // Buscar opciones mientras escribe (debounce)
  useEffect(() => {
    if (!open) return;

    const term = (csInput || "").trim();

    if (term.length > 0 && term.length < 2) {
      setCsOptions([]);
      return;
    }

    const t = setTimeout(async () => {
      try {
        setCsLoading(true);
        const resp = await searchClienteServicio({
          codServicio: 2,
          limit: 20,
          nombreCliente: term,
          estado: "Activo",
        });

        const data = Array.isArray(resp?.data) ? resp.data : [];
        setCsOptions(data);
      } catch (e) {
        setCsOptions([]);
      } finally {
        setCsLoading(false);
      }
    }, 400);

    return () => clearTimeout(t);
  }, [csInput, open]);

  // Si csSelected no tiene nombre, traemos el registro exacto por ID — en
  // EDIT porque solo llega el cod_servicio_cliente guardado localmente, y en
  // CREATE cuando initialClienteServicio solo trae el id (sin nombre_cliente
  // pre-cargado por el caller).
  useEffect(() => {
    if (!open) return;

    const selectedId = csSelected?.cod_servicio_cliente;
    const selectedName = csSelected?.nombre_cliente;

    const id = Number((isEdit ? editCodServicio : codServicioCliente) || selectedId);
    if (!id) return;

    const hasName = !!(selectedName || "").trim();
    if (hasName) return;

    let cancelled = false;

    (async () => {
      try {
        setCsLoading(true);

        const resp = await getClienteServicioById(id);
        if (cancelled) return;

        const data = resp?.data || null;

        if (data?.cod_servicio_cliente) {
          setCsSelected(data);
          setCsInput(data.nombre_cliente || "");
        }
      } catch (e) {
        // fallback silencioso
      } finally {
        if (!cancelled) setCsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    isEdit,
    editCodServicio,
    codServicioCliente,
    csSelected?.cod_servicio_cliente,
    csSelected?.nombre_cliente,
  ]);

  const pwdError = useMemo(
    () => validatePasswordChange(newPassword),
    [newPassword]
  );

  // Payload edit (solo envía lo que cambió)
  const payloadEdit = useMemo(() => {
    if (!isEdit) return null;

    const p = { userId: Number(userId) };

    // name solo si cambió
    if (
      (editName || "").trim() &&
      (editName || "").trim() !== (initialUser?.nm || "")
    ) {
      p.name = editName.trim();
    }

    // cod_servicio_cliente solo si cambió
    const originalCod = initialUser?.local?.cod_servicio_cliente;
    if (editCodServicio && Number(editCodServicio) !== Number(originalCod)) {
      p.cod_servicio_cliente = Number(editCodServicio);
    }

    // enabled solo si cambió
    const originalEnabled = initialUser?.local?.estado
      ? String(initialUser.local.estado).toLowerCase() === "activo"
      : true;

    if (enabled !== originalEnabled) {
      p.enabled = enabled;
    }

    // ✅ password solo si escribió nueva
    const wantsPwd = !!(newPassword || "").trim();
    if (wantsPwd) {
      p.newPassword = newPassword;
      p.reset = true;

      // ✅ Compatibilidad si tu backend/service aún exige oldPassword:
      // Si ya modificaste el backend para NO exigir oldPassword con reset=true, puedes borrar esta línea.
      p.oldPassword = "x";
    }

    return p;
  }, [
    isEdit,
    userId,
    editName,
    editCodServicio,
    enabled,
    newPassword,
    initialUser,
  ]);

  const canSave = useMemo(() => {
    if (isEdit) {
      if (!userId) return false;
      if (pwdError) return false;

      const keys = Object.keys(payloadEdit || {});
      return keys.some((k) => k !== "userId");
    }

    return !!String(codServicioCliente).trim() && !!name.trim() && !!password;
  }, [isEdit, userId, pwdError, payloadEdit, codServicioCliente, name, password]);

  // Cuentas disponibles para el cliente elegido; si una tiene el mismo nombre que su razón social, queda preseleccionada.
  useEffect(() => {
    if (!open || !usaCuentas || !codServicioCliente) {
      setCuentas([]);
      setCuentaSel(null);
      return undefined;
    }
    let vivo = true;
    setCuentasCargando(true);
    lcListCuentas(codServicioCliente)
      .then(({ data }) => {
        if (!vivo) return;
        const lista = Array.isArray(data?.cuentas) ? data.cuentas : [];
        setCuentas(lista);
        setCuentasInfo({ razon_social: data?.razon_social || "", sucursal: data?.sucursal || null });
        const nueva = data?.cuenta_nueva || null;
        setCuentaNueva(nueva);
        // Predeterminada: la primera cuenta de los usuarios que el cliente ya tiene; si no tiene usuarios, la que se llama como el cliente (si existe);
        // y si tampoco existe, una cuenta nueva con el nombre del cliente.
        const primera = lista.find((c) => c.del_cliente && c.manager_id);
        const sugeridas = lista.filter((c) => c.sugerida && c.manager_id);
        const nuevaOpt = nueva?.disponible ? { id: "nueva", nueva: true, nombre: nueva.nombre, manager_id: true, usuarios: 0 } : null;
        setCuentaSel(primera || (sugeridas.length === 1 ? sugeridas[0] : nuevaOpt));
      })
      .catch(() => { if (vivo) { setCuentas([]); setCuentaNueva(null); setCuentaSel(null); } })
      .finally(() => { if (vivo) setCuentasCargando(false); });
    return () => { vivo = false; };
  }, [open, usaCuentas, codServicioCliente]);

  const handleSave = async () => {
    setErr("");
    try {
      setLoading(true);

      if (isEdit) {
        const resp = await updateUsuario(payloadEdit);
        onSuccess?.(resp?.data);
      } else {
        const body = {
          cod_servicio_cliente: Number(codServicioCliente),
          name: name.trim(),
          password,
          ...(usaCuentas && cuentaSel ? (cuentaSel.nueva ? { cuenta_nueva: true } : { cuenta_id: cuentaSel.id }) : {}),
        };
        const resp = await createUsuario(body);
        onSuccess?.(resp?.data);
        if (Array.isArray(resp?.data?.avisos) && resp.data.avisos.length) {
          setAvisosCreacion(resp.data.avisos);
          return; // se queda abierto para mostrar los avisos; "Cerrar" lo cierra
        }
      }

      onClose?.();
    } catch (e) {
      setErr(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e.message ||
          "Error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          minHeight: "auto",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, typography: "h6", fontWeight: 600 }}>
        {isEdit ? `Editar usuario ${providerName}` : `Nuevo usuario ${providerName}`}
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5, pb: 2.5, px: 2.5 }}>
        <form autoComplete="off">
          {/* inputs hidden anti-autofill iguales */}

          {!!err && (
            <Alert severity="error" sx={{ mb: 2.5 }} variant="outlined">
              {err}
            </Alert>
          )}

          {avisosCreacion.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2.5 }} variant="outlined">
              <strong>El usuario se creó y quedó vinculado al cliente, pero:</strong>
              {avisosCreacion.map((a) => <Box key={a} sx={{ mt: 0.5 }}>{a}</Box>)}
            </Alert>
          )}

          <Grid container spacing={1.25}>
            {/* Sección principal */}
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Información básica
              </Typography>
            </Grid>

            {isEdit && (
              <Grid size={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="ID"
                  value={userId}
                  disabled
                  variant="outlined"
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label={isEdit ? "Nombre" : "Usuario (name)"}
                value={isEdit ? editName : name}
                onChange={(e) =>
                  isEdit ? setEditName(e.target.value) : setName(e.target.value)
                }
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {isEdit && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: enabled ? 600 : 400 }}>
                        {enabled ? "Activo" : "Inactivo"}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
              </Grid>
            )}

            <Grid size={12}>
              {mostrarVinculacionesFijas ? (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Cliente / Servicio — {vinculaciones.length} vinculado{vinculaciones.length === 1 ? "" : "s"}
                  </Typography>
                  <Box sx={{ maxHeight: 180, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    <List dense disablePadding>
                      {vinculaciones.map((v) => (
                        <ListItem key={v.id_vinculacion} divider>
                          <ListItemText
                            primary={`${v.razon_social || v.cod_cliente || "-"}${v.descripcion ? ` - ${v.descripcion}` : ""}`}
                            secondary={`Servicio #${v.cod_servicio_cliente}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </>
              ) : !isEdit && initialClienteServicio?.cod_servicio_cliente != null ? (
                <Typography variant="caption" color="text.secondary">
                  Cliente: {csLoading ? "Cargando..." : (getCsLabel(csSelected) || `#${initialClienteServicio.cod_servicio_cliente}`)}
                </Typography>
              ) : (
                <Autocomplete
                  fullWidth
                  size="small"
                  disabled={isEdit}
                  value={csSelected}
                  options={csOptions}
                  loading={csLoading}
                  inputValue={csInput}
                  onInputChange={(e, v) => setCsInput(v)}
                  onChange={(e, opt) => {
                    setCsSelected(opt);
                    const setter = isEdit ? setEditCodServicio : setCodServicioCliente;
                    setter(opt?.cod_servicio_cliente ? String(opt.cod_servicio_cliente) : "");
                  }}
                  isOptionEqualToValue={(a, b) =>
                    Number(a?.cod_servicio_cliente) === Number(b?.cod_servicio_cliente)
                  }
                  getOptionLabel={getCsLabel}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Cliente / Servicio"
                      placeholder="Buscar por nombre..."
                      variant="outlined"
                      helperText={isEdit ? `Actual: ${editCodServicio || "—"}` : " "}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {csLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            </Grid>

            {usaCuentas && codServicioCliente && (
              <Grid size={12}>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={cuentaSel}
                  options={opcionesCuenta}
                  loading={cuentasCargando}
                  onChange={(e, c) => setCuentaSel(c)}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  getOptionLabel={(c) => c?.nombre || ""}
                  groupBy={(c) => (c.nueva ? "Cuenta nueva" : c.del_cliente ? "Cuentas de los usuarios del cliente" : "Otras cuentas")}
                  getOptionDisabled={(c) => !c.nueva && !c.manager_id}
                  noOptionsText="No hay cuentas disponibles"
                  renderOption={(props, c) => (
                    <li {...props} key={c.id}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {c.nombre}
                          {c.del_cliente && c.usuarios_cliente?.length ? <Typography component="span" variant="caption" color="text.secondary"> · {c.usuarios_cliente.join(", ")}</Typography> : null}
                        </Typography>
                        {c.nueva && <Chip size="small" color="primary" label="Se creará" sx={{ height: 18, fontSize: 10 }} />}
                        {!c.nueva && !c.manager_id && <Chip size="small" color="warning" variant="outlined" label="Sin administrador" sx={{ height: 18, fontSize: 10 }} />}
                        {!c.del_cliente && c.sugerida && <Chip size="small" color="success" label="Coincide con el cliente" sx={{ height: 18, fontSize: 10 }} />}
                        {c.parecida && <Chip size="small" color="info" variant="outlined" label="Parecida" sx={{ height: 18, fontSize: 10 }} />}
                        {!c.nueva && <Typography variant="caption" color="text.secondary">{c.usuarios} usuario(s)</Typography>}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Cuenta de Leasecontrol"
                      placeholder="Sin cuenta propia"
                      helperText={
                        cuentaSel
                          ? (cuentaSel.nueva
                            ? `Se creará la cuenta "${cuentaSel.nombre}" y este usuario será su administrador. La cuenta quedará dentro de la cuenta de la sucursal${cuentasInfo.sucursal ? ` (${cuentasInfo.sucursal})` : ""}.`
                            : cuentaSel.del_cliente
                            ? `Cuenta de los usuarios que ya tiene este cliente (${(cuentaSel.usuarios_cliente || []).join(", ")}).`
                            : cuentaSel.sugerida ? "Coincide con la razón social del cliente." : "El usuario se creará dentro de esta cuenta.")
                          : `Este cliente todavía no tiene una cuenta propia en Leasecontrol${cuentasInfo.razon_social ? ` (cuenta "${cuentasInfo.razon_social}" sin crear)` : ""}. El usuario quedará en la cuenta de la sucursal${cuentasInfo.sucursal ? ` (${cuentasInfo.sucursal})` : ""}.`
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {cuentasCargando ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid size={12} sx={{ my: 1 }}>
              <Divider />
            </Grid>

            {/* Sección contraseña */}
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                {isEdit ? "Cambio de contraseña (opcional)" : "Contraseña"}
              </Typography>
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label={isEdit ? "Nueva contraseña" : "Contraseña"}
                type={showPassword ? "text" : "password"}
                value={isEdit ? newPassword : password}
                onChange={(e) =>
                  isEdit ? setNewPassword(e.target.value) : setPassword(e.target.value)
                }
                variant="outlined"
                autoComplete="new-password"
                error={!!pwdError}
                helperText={pwdError || PASSWORD_HINT}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={() => setShowPassword((prev) => !prev)}
                        sx={{ minWidth: 0, p: 0.5 }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
        {avisosCreacion.length > 0 ? (
          <Button onClick={onClose} variant="contained" disableElevation size="medium">
            Cerrar
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={loading} size="medium">
              Cancelar
            </Button>
            <LoadingButton
              loading={loading}
              variant="contained"
              disableElevation
              onClick={handleSave}
              disabled={!canSave}
              size="medium"
            >
              Guardar
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
